/**
 * Pure timeline operations for the Studio editor.
 *
 * Every edit the UI can make is a function from clips to clips, so the editor
 * component stays a thin hand on top of testable arithmetic — and undo is a
 * stack of past arrays rather than an inverse operation per gesture, which is
 * the difference between undo that always works and undo that mostly works.
 *
 * Times follow the same rule as everywhere else: source milliseconds live on
 * the clip, output milliseconds are derived from order. Nothing here stores an
 * output time, which is why reordering is `splice` and nothing else moves.
 */
import {
  MIN_CLIP_MS,
  clipOutputOffsets,
  timelineDurationMs,
  type Clip,
  type Transition,
} from "@rec/shared";

export const clipDurationMs = (clip: Clip): number =>
  clip.sourceEndMs - clip.sourceStartMs;

/**
 * Duration and offsets come from the shared EDL math, which is transition-
 * aware: a transition overlaps its two clips and *shortens* the output. The
 * editor must use the same arithmetic the renderer and transcript mapper use,
 * or the playhead, chapter positions and published timestamps all drift by
 * the sum of the fades.
 */
export const totalDurationMs = (clips: Clip[]): number =>
  timelineDurationMs({ version: 1, clips });

/** Output start of each clip, index-aligned with `clips`. Overlap-aware. */
export const outputOffsets = (clips: Clip[]): number[] =>
  clipOutputOffsets({ version: 1, clips });

export type Located = {
  index: number;
  clip: Clip;
  /** Position within the clip's source, ready to seek a player to. */
  sourceMs: number;
  /** The clip's own start in output time. */
  outputStartMs: number;
};

/**
 * Which clip contains this output moment. Null on an empty timeline.
 *
 * During a transition's overlap two clips are genuinely on screen at once; the
 * *incoming* clip claims the moment (the latest clip whose offset has been
 * reached), which matches what a viewer would say is playing by the midpoint
 * of the fade — and keeps split/seek deterministic.
 */
export function locate(clips: Clip[], outputMs: number): Located | null {
  if (clips.length === 0) return null;

  const offsets = outputOffsets(clips);

  let index = 0;
  for (let i = 0; i < clips.length; i++) {
    if (outputMs >= offsets[i]!) index = i;
  }

  const clip = clips[index]!;
  const sourceMs = Math.min(
    clip.sourceEndMs,
    Math.max(clip.sourceStartMs, clip.sourceStartMs + (outputMs - offsets[index]!)),
  );

  return { index, clip, sourceMs, outputStartMs: offsets[index]! };
}

const freshId = () =>
  (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)).slice(0, 32);

/**
 * Split the clip under the playhead into two at that exact frame.
 *
 * The primary authoring gesture: play, press S where the topic changes. A
 * split near an edge that would leave a fragment shorter than `MIN_CLIP_MS`
 * does nothing rather than creating a sliver that fails validation on save.
 */
export function splitAt(clips: Clip[], outputMs: number): Clip[] {
  const hit = locate(clips, outputMs);
  if (!hit) return clips;

  const { index, clip, sourceMs } = hit;
  if (
    sourceMs - clip.sourceStartMs < MIN_CLIP_MS ||
    clip.sourceEndMs - sourceMs < MIN_CLIP_MS
  ) {
    return clips;
  }

  const next = [...clips];
  next.splice(
    index,
    1,
    { ...clip, sourceEndMs: Math.round(sourceMs) },
    // The trailing half must NOT inherit `transitionIn`: that field means
    // "how this clip is entered from the previous one", and the previous one
    // is now footage it is physically continuous with. Carrying it would turn
    // a requested cut into a dissolve of the shot into itself — and shorten
    // the output by the fade, silently.
    {
      ...clip,
      id: freshId(),
      sourceStartMs: Math.round(sourceMs),
      transitionIn: undefined,
    },
  );
  return next;
}

export function removeClip(clips: Clip[], id: string): Clip[] {
  return clips.filter((clip) => clip.id !== id);
}

/** Insert the copy right after the original — where a duplicate reads as one. */
export function duplicateClip(clips: Clip[], id: string): Clip[] {
  const index = clips.findIndex((clip) => clip.id === id);
  if (index === -1) return clips;

  const next = [...clips];
  // Same rule as splitAt: the copy sits right after the original, entering
  // from identical footage — a transition there is a dissolve nobody placed.
  next.splice(index + 1, 0, {
    ...clips[index]!,
    id: freshId(),
    transitionIn: undefined,
  });
  return next;
}

/**
 * Move the clip at `from` so it sits before the clip currently at `to`
 * (`to === clips.length` moves it to the end).
 *
 * `to` is a PRE-removal index — "insert before the row/block you are
 * pointing at" — because that is what both the strip and the list compute
 * from midpoints. Splicing after removal shifts everything past `from` left
 * by one, so a downward move must subtract its own removal; not doing so
 * made every downward drag land one slot past the seam.
 *
 * Returns `clips` unchanged (same reference) when the move is a no-op, so
 * callers can skip committing a history step for a drag that went nowhere.
 */
export function moveClip(clips: Clip[], from: number, to: number): Clip[] {
  if (from < 0 || from >= clips.length) return clips;
  const insertAt = to > from ? to - 1 : to;
  if (insertAt === from) return clips;
  const next = [...clips];
  const [moved] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(insertAt, next.length)), 0, moved!);
  return next;
}

/**
 * Drag a clip's edge. Clamped three ways: never outside the source media,
 * never shorter than `MIN_CLIP_MS`, and always integer milliseconds.
 */
export function trimClip(
  clips: Clip[],
  id: string,
  edge: "start" | "end",
  toSourceMs: number,
  sourceDurationMs: number,
): Clip[] {
  return clips.map((clip) => {
    if (clip.id !== id) return clip;

    const value = Math.round(toSourceMs);
    if (edge === "start") {
      const clamped = Math.max(
        0,
        Math.min(value, clip.sourceEndMs - MIN_CLIP_MS),
      );
      return { ...clip, sourceStartMs: clamped };
    }
    const clamped = Math.min(
      sourceDurationMs,
      Math.max(value, clip.sourceStartMs + MIN_CLIP_MS),
    );
    return { ...clip, sourceEndMs: clamped };
  });
}

/** Append a whole recording (or a span of one) to the end of the timeline. */
export function appendClip(
  clips: Clip[],
  recordingId: string,
  sourceDurationMs: number,
): Clip[] {
  return [
    ...clips,
    {
      id: freshId(),
      recordingId,
      sourceStartMs: 0,
      sourceEndMs: Math.max(sourceDurationMs, MIN_CLIP_MS),
    },
  ];
}

/** Name one part. Empty restores the fallback (source name). */
export function setClipLabel(clips: Clip[], id: string, label: string): Clip[] {
  const trimmed = label.trim();
  return clips.map((clip) =>
    clip.id === id ? { ...clip, label: trimmed ? trimmed : undefined } : clip,
  );
}

/** Silence one clip's audio in the output. The video keeps playing. */
export function toggleMute(clips: Clip[], id: string): Clip[] {
  return clips.map((clip) =>
    clip.id === id ? { ...clip, muted: clip.muted ? undefined : true } : clip,
  );
}

/**
 * Set how a clip is entered from its predecessor. `undefined` restores the
 * hard cut. Stored on the incoming clip, so reordering keeps the transition
 * with the clip it introduces — the same rule as the EDL schema.
 */
export function setTransition(
  clips: Clip[],
  id: string,
  transition: Transition | undefined,
): Clip[] {
  return clips.map((clip) =>
    clip.id === id ? { ...clip, transitionIn: transition } : clip,
  );
}

/**
 * Pull a value onto the nearest snap target when it is close enough.
 *
 * Targets are clip boundaries and the playhead; the threshold is given in
 * output milliseconds by the caller, derived from zoom — snapping is a
 * *visual* affordance, so "close" means close on screen, not close in time.
 */
export function snap(valueMs: number, targets: number[], thresholdMs: number): number {
  let best = valueMs;
  let bestDistance = thresholdMs;

  for (const target of targets) {
    const distance = Math.abs(target - valueMs);
    if (distance < bestDistance) {
      best = target;
      bestDistance = distance;
    }
  }
  return best;
}

// ─── Undo ────────────────────────────────────────────────────────────────────

export type EditorSnapshot = {
  clips: Clip[];
  chapters: Array<{ title: string; summary: string; startMs: number; endMs: number }>;
};

export type History = {
  past: EditorSnapshot[];
  future: EditorSnapshot[];
};

const HISTORY_LIMIT = 100;

export function pushHistory(history: History, snapshot: EditorSnapshot): History {
  return {
    past: [...history.past.slice(-HISTORY_LIMIT + 1), snapshot],
    // A new edit forks the timeline: whatever was redoable no longer follows.
    future: [],
  };
}

export function undo(
  history: History,
  current: EditorSnapshot,
): { history: History; snapshot: EditorSnapshot } | null {
  const previous = history.past[history.past.length - 1];
  if (!previous) return null;

  return {
    history: { past: history.past.slice(0, -1), future: [current, ...history.future] },
    snapshot: previous,
  };
}

export function redo(
  history: History,
  current: EditorSnapshot,
): { history: History; snapshot: EditorSnapshot } | null {
  const [next, ...rest] = history.future;
  if (!next) return null;

  return {
    history: { past: [...history.past, current], future: rest },
    snapshot: next,
  };
}
